import React, { useMemo, useState } from "react";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import { useResolvedDark } from "../../../ThemeProvider/ThemeContext";

const TopSentimentSummary = ({
  positives = [],
  negatives = [],
  onSentimentClick,
  isDark: isDarkProp,
}) => {
  const isDark = useResolvedDark(isDarkProp);
  const [activeView, setActiveView] = useState("positive");
  const [hovered, setHovered] = useState(null);
  const isMobile = window.innerWidth < 768;

  const theme = useMemo(() => {
    if (isDark) {
      return {
        barBg: "#0F1424",
        barBorder: "#232B4A",
        text: "#E8ECF8",
        muted: "#A6B0CF",

        posChipBg: "rgba(46, 204, 113, 0.14)",
        posChipBorder: "rgba(46, 204, 113, 0.25)",
        posChipText: "#B9F6C9",
        posIcon: "#2ECC71",

        negChipBg: "rgba(231, 76, 60, 0.14)",
        negChipBorder: "rgba(231, 76, 60, 0.25)",
        negChipText: "#FFC2BC",
        negIcon: "#E74C3C",

        inactiveIcon: "#4B5578",
        focusRing: "0 0 0 3px rgba(76,125,255,0.25)",
      };
    }
    return {
      barBg: "#FAFAFA",
      barBorder: "#E9ECEF",
      text: "#0B1220",
      muted: "#5B6472",

      posChipBg: "#E0F6ED",
      posChipBorder: "#BFEAD6",
      posChipText: "#155724",
      posIcon: "#28A745",

      negChipBg: "#FDE8E8",
      negChipBorder: "#F6C7C7",
      negChipText: "#721C24",
      negIcon: "#DC3545",

      inactiveIcon: "#C7CDD6",
      focusRing: "0 0 0 3px rgba(13,110,253,0.18)",
    };
  }, [isDark]);

  const renderSentiment = (data, type) => {
    const isPositive = type === "positive";
    const chipBg = isPositive ? theme.posChipBg : theme.negChipBg;
    const chipBorder = isPositive ? theme.posChipBorder : theme.negChipBorder;
    const chipText = isPositive ? theme.posChipText : theme.negChipText;

    return (
      <div className="d-flex flex-nowrap overflow-auto gap-2" style={{ padding: "4px 0" }}>
        {data.map((item, idx) => {
          const countryLabel = (item.country || "")
            .split(" ")
            .filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");

          return (
            <button
              key={`${type}-${idx}`}
              type="button"
              className="flex-shrink-0"
              onClick={() => onSentimentClick?.(item.company, item.country)}
              style={{
                backgroundColor: chipBg,
                color: chipText,
                borderRadius: "10px",
                padding: "6px 10px",
                whiteSpace: "nowrap",
                fontSize: "0.85rem",
                cursor: "pointer",
                border: `1px solid ${chipBorder}`,
                outline: "none",
              }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = theme.focusRing)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
            >
              {item.company} ({countryLabel}) : {Number(item.score).toFixed(2)}
            </button>
          );
        })}
      </div>
    );
  };

  const activeIsPositive = activeView === "positive";
  const activeIsNegative = activeView === "negative";

  return (
    <div
      className="d-flex align-items-center justify-content-between px-3"
      style={{
        backgroundColor: theme.barBg,
        height: "48px",
        overflow: "hidden",
        borderTop: `1px solid ${theme.barBorder}`,
      }}
    >
      {/* Left Icon with Expandable Text */}
      <div
        className="d-flex align-items-center justify-content-start "
        style={{
          width: hovered === "positive" ? (!isMobile ? "128px" : "34px") :  "30px",
          cursor: "pointer",
          transition: "width 0.25s ease",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
        onClick={() => setActiveView("positive")}
        onMouseEnter={() => setHovered("positive")}
        onMouseLeave={() => setHovered(null)}
        title="Show Top Positive Sentiments"
      >
        <FaArrowUp
          size={16}
          color={activeIsPositive ? theme.posIcon : theme.inactiveIcon}
          className= {isMobile ? "" : "me-1"}
        />
        {(hovered === "positive" && !isMobile) && (
          <span
            style={{
              color: activeIsPositive ? theme.posIcon : theme.muted,
              opacity: 1,
              transition: "opacity 0.15s ease",
              fontSize: "0.85rem",
            }}
          >
            Top Positive
          </span>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-grow-1 mx-2 overflow-x-auto">
        {activeIsPositive
          ? renderSentiment(positives, "positive")
          : renderSentiment(negatives, "negative")}
      </div>

      {/* Right Icon with Expandable Text */}
      <div
        className="d-flex align-items-center justify-content-end" 
        style={{
          width: hovered === "negative" ? (!isMobile ? "128px" : "34px") :  "30px",
          cursor: "pointer",
          transition: "width 0.25s ease",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
        onClick={() => setActiveView("negative")}
        onMouseEnter={() => setHovered("negative")}
        onMouseLeave={() => setHovered(null)}
        title="Show Top Negative Sentiments"
      >
        <FaArrowDown
          size={16}
          color={activeIsNegative ? theme.negIcon : theme.inactiveIcon}
          className= {isMobile ? "" : "me-1"}
        />
        {(hovered === "negative" && !isMobile) && (
          <span
            style={{
              color: activeIsNegative ? theme.negIcon : theme.muted,
              opacity: 1,
              transition: "opacity 0.15s ease",
              fontSize: "0.85rem",
            }}
          >
            Top Negative
          </span>
        )}
      </div>
    </div>
  );
};

export default TopSentimentSummary;
